import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { Logo } from "./Logo";
import { signInWithGoogle, checkOwner, formatApiErrorDetail, savePin } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignIn() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | register | owner
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const destination = async () => {
    const owner = await checkOwner();
    navigate(owner ? "/inbox" : "/my-games");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "register") {
        await axios.post(`${API}/auth/register`, { name, email, password: pw }, { withCredentials: true });
      } else {
        await axios.post(`${API}/auth/login`, { email, password: pw }, { withCredentials: true });
      }
      await destination();
    } catch (error) {
      setErr(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
    setBusy(false);
  };

  const submitOwner = async (e) => {
    e.preventDefault();
    const entered = pin.replace(/\s/g, "").trim();
    if (!entered) {
      setPinError(true);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await axios.get(`${API}/schedule/pin-check`, { headers: { "x-schedule-pin": entered } });
      savePin(entered);
      navigate("/inbox");
    } catch {
      setPinError(true);
    }
    setBusy(false);
  };

  const switchMode = (m) => {
    setMode(m);
    setErr("");
    setPinError(false);
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-ink px-6 py-12 font-body text-white"
      data-testid="signin-page"
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/5 bg-surface p-8 text-center">
        <span className="text-accentlight">
          <Logo className="mx-auto h-12 w-12" />
        </span>
        <h1 className="mt-5 font-display text-3xl tracking-wider">
          {mode === "owner" ? "OWNER SIGN-IN" : mode === "register" ? "JOIN THE ROSTER" : "WELCOME BACK"}
        </h1>
        <p className="mt-2 text-sm text-white/50">
          {mode === "owner"
            ? "Enter your PIN to open the booking inbox."
            : mode === "register"
            ? "Create an account — your game details are saved for next time."
            : "Sign in to see your games and book again in seconds."}
        </p>

        {mode !== "owner" && (
          <button
            type="button"
            onClick={() => switchMode("owner")}
            data-testid="signin-owner-button"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-accentlight/40 py-3 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10"
          >
            <Lock className="h-3.5 w-3.5" /> Owner Sign-In
          </button>
        )}

        {mode !== "owner" && (
          <>
            <button
              type="button"
              onClick={() => signInWithGoogle("/auth/dest")}
              data-testid="signin-google-button"
              className="mt-3 flex w-full items-center justify-center gap-3 rounded-full bg-white py-3.5 text-sm font-bold text-[#1f1f1f] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)]"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.6z" />
                <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.8-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5H1.4v3C3.3 21.3 7.3 24 12 24z" />
                <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4v-3H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.8-3z" />
                <path fill="#EA4335" d="M12 4.8c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 5.8 2.1 4.6 5H1.4v3H8z" />
              </svg>
              Continue with Google
            </button>

            <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/30">
              <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
            </div>
          </>
        )}

        {mode === "owner" ? (
          <form onSubmit={submitOwner} data-testid="owner-pin-form">
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter your PIN"
              required
              data-testid="owner-pin-input"
              className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-center text-lg tracking-[0.5em] text-white placeholder:text-base placeholder:tracking-normal placeholder:text-white/30 outline-none focus:border-accentlight"
            />
            {pinError && (
              <p className="mt-3 text-xs text-red-300" data-testid="owner-pin-error">
                That PIN didn't work — try again.
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="owner-pin-submit-button"
              className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
            >
              Unlock inbox
            </button>
            <button
              type="button"
              onClick={() => switchMode("signin")}
              data-testid="owner-back-link"
              className="mt-4 text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
            >
              Back to customer sign-in
            </button>
          </form>
        ) : mode === "register" ? (
          <form onSubmit={submit} data-testid="signin-form">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              required
              data-testid="signin-name-input"
              className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              required
              data-testid="signin-email-input"
              className="mt-3 w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
            />
            <div className="relative mt-3">
              <input
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Create a password (min 8 characters)"
                autoComplete="new-password"
                required
                minLength={8}
                data-testid="signin-password-input"
                className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="signin-password-toggle"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-white"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {err && (
              <p className="mt-3 text-xs text-red-300" data-testid="signin-error">
                {err}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="signin-submit-button"
              className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
            >
              Create account
            </button>
          </form>
        ) : (
          <form onSubmit={submit} data-testid="signin-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              required
              data-testid="signin-email-input"
              className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
            />
            <div className="relative mt-3">
              <input
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                required
                data-testid="signin-password-input"
                className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="signin-password-toggle"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-white"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {err && (
              <p className="mt-3 text-xs text-red-300" data-testid="signin-error">
                {err}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="signin-submit-button"
              className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
            >
              Sign in
            </button>
          </form>
        )}

        {mode === "signin" && (
          <button
            type="button"
            onClick={() => switchMode("register")}
            data-testid="signin-mode-toggle"
            className="mt-4 text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
          >
            New here? Create an account
          </button>
        )}
        {mode === "register" && (
          <button
            type="button"
            onClick={() => switchMode("signin")}
            data-testid="signin-mode-toggle"
            className="mt-4 text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
          >
            Already have an account? Sign in
          </button>
        )}

        <a
          href="/"
          className="mt-4 block text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
        >
          Back to site
        </a>
      </div>
    </div>
  );
}